import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { productsPlaceholderList } from '@/api/generated/products-placeholders/products-placeholders';
import type { PlaceholderListItem } from '@/api/generated/schemas';
import { Badge } from '@/shared/ui';
import { col, filter, type ListPageConfig } from '@/shared/ui/list-page';
import { NavListPage } from '@/shared/navigation/react/NavListPage';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { destinationSelector } from '@/modules/products/venua-planner/components/DestinationSelector';
import type { DestinationRef } from '@/modules/products/venua-planner/venuaPlannerApi';
import {
  placeholderSubcategorySelector,
  type PlaceholderSubcategoryRef,
} from '@/modules/products/venua-planner/components/PlaceholderSubcategorySelector';
import { placeholderDetailAgenda } from '../placeholderDetail/placeholderDetailAgenda';
import { placeholderListAgenda } from './placeholderListAgenda';
import { PLACEHOLDER_LIST_KEY } from '../internal/schemesQueryKeys';
import {
  placeholderStatusOptions,
  placeholderTypeOptions,
  statusTone,
  type EnumOption,
} from '../internal/placeholderEnums';
import { staticEnumOptions } from '../internal/staticEnumOptions';

/** Lokalizovaný popisek enum hodnoty z voleb (fallback = původní hodnota). */
function enumLabel<T extends string>(options: EnumOption<T>[], value: T): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

function buildPlaceholderListConfig(
  openPlaceholder: (id: string | undefined) => void,
  t: TFunction
): ListPageConfig<PlaceholderListItem> {
  const typeOptions = placeholderTypeOptions(t);
  const statusOptions = placeholderStatusOptions(t);

  return {
    key: PLACEHOLDER_LIST_KEY,
    i18n: { namespace: 'venua-planner' },
    api: { list: (request, signal) => productsPlaceholderList(request, signal) },
    rowKey: (item) => item.id,
    defaultSort: { column: 'displayName', desc: false },
    fulltext: { placeholder: (tt) => tt('schemes.placeholderList.search') },
    onOpen: (item) => openPlaceholder(item.id),
    create: {
      label: (tt) => tt('schemes.placeholderList.new'),
      onClick: () => openPlaceholder(undefined),
    },
    columns: (tt) => [
      col.custom<PlaceholderListItem>({
        key: 'displayName',
        label: tt('schemes.placeholderList.cols.displayName'),
        w: 240,
        frozen: true,
        cell: (item) => (
          <span className="font-semibold">
            {item.displayName ?? tt('schemes.placeholderList.noName')}
          </span>
        ),
      }),
      col.custom<PlaceholderListItem>({
        key: 'type',
        label: tt('schemes.placeholderList.cols.type'),
        w: 140,
        cell: (item) => <Badge tone="info">{enumLabel(typeOptions, item.type)}</Badge>,
      }),
      col.text<PlaceholderListItem>('subcategoryName', {
        label: tt('schemes.placeholderList.cols.subcategory'),
        w: 180,
      }),
      col.text<PlaceholderListItem>('destinationName', {
        label: tt('schemes.placeholderList.cols.destination'),
        w: 160,
      }),
      col.custom<PlaceholderListItem>({
        key: 'status',
        label: tt('schemes.placeholderList.cols.status'),
        w: 120,
        cell: (item) => (
          <Badge tone={statusTone(item.status)}>{enumLabel(statusOptions, item.status)}</Badge>
        ),
      }),
      col.mono<PlaceholderListItem>('id', {
        label: tt('schemes.placeholderList.cols.id'),
        w: 90,
      }),
    ],
    filterGroups: (tt) => [{ id: 'basic', label: tt('schemes.placeholderList.groups.basic') }],
    filters: (tt) => [
      filter.text('name', {
        label: tt('schemes.placeholderList.filters.name'),
        group: 'basic',
        freq: true,
      }),
      filter.enum('type', {
        label: tt('schemes.placeholderList.filters.type'),
        group: 'basic',
        freq: true,
        options: staticEnumOptions(typeOptions),
      }),
      filter.enum('status', {
        label: tt('schemes.placeholderList.filters.status'),
        group: 'basic',
        freq: true,
        options: staticEnumOptions(statusOptions),
      }),
      filter.selector<DestinationRef>('destination', {
        label: tt('schemes.placeholderList.filters.destination'),
        group: 'basic',
        selector: destinationSelector,
        getId: (item) => item.destinationId,
        getLabel: (item) => item.name,
      }),
      filter.selector<PlaceholderSubcategoryRef>('subcategory', {
        label: tt('schemes.placeholderList.filters.subcategory'),
        group: 'basic',
        selector: placeholderSubcategorySelector,
        getId: (item) => item.id,
        getLabel: (item) => item.name,
      }),
    ],
  };
}

/**
 * ListPage knihovny placeholderů. Otevření řádku i „Nový placeholder" míří na
 * `placeholderDetailAgenda` (modální vrstva).
 */
export function PlaceholderListPage({ layerId }: { layerId: string }) {
  const { t } = useTranslation('venua-planner');
  const navigator = useNavigator();

  const openPlaceholder = useCallback(
    (id: string | undefined) => {
      void navigator.openModal(placeholderDetailAgenda, { placeholderId: id });
    },
    [navigator]
  );

  const config = useMemo(
    () => buildPlaceholderListConfig(openPlaceholder, t),
    [openPlaceholder, t]
  );

  return <NavListPage agenda={placeholderListAgenda} layerId={layerId} config={config} />;
}
